import styles from './Statistics.module.css';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Line } from '@antv/g2plot';
import { Dropdown, Card, Row, Col, Spin, PageHeader, Button, Menu, List } from 'antd';

import { ReloadOutlined, DownOutlined } from '@ant-design/icons';

import { getUsers, getForms, getSubmissions, getTopViewedForms, getTopUsedThemes } from 'store/ducks/statistics.js';

const Statistics = () => {
  const dispatch = useDispatch();

  const [selectedDays, setSelectedDays] = useState(30);

  const usersChartRef = React.createRef();
  const formsChartRef = React.createRef();
  const submissionsChartRef = React.createRef();

  const users = useSelector((state) => state.statistics.users);
  const getUsersLoading = useSelector((state) => state.statistics.getUsers.loading);

  const forms = useSelector((state) => state.statistics.forms);
  const getFormsLoading = useSelector((state) => state.statistics.getForms.loading);

  const submissions = useSelector((state) => state.statistics.submissions);
  const getSubmissionsLoading = useSelector((state) => state.statistics.getSubmissions.loading);

  const topViewedForms = useSelector((state) => state.statistics.topViewedForms);
  const getTopViewedFormsLoading = useSelector((state) => state.statistics.getTopViewedForms.loading);

  const topUsedThemes = useSelector((state) => state.statistics.topUsedThemes);
  const getTopUsedThemesLoading = useSelector((state) => state.statistics.getTopUsedThemes.loading);

  const reload = (days) => {
    dispatch(getUsers(days || selectedDays));
    dispatch(getForms(days || selectedDays));
    dispatch(getSubmissions(days || selectedDays));
    dispatch(getTopViewedForms());
    dispatch(getTopUsedThemes());
  };

  useEffect(() => {
    if (users === null) dispatch(getUsers(selectedDays));
    if (forms === null) dispatch(getForms(selectedDays));
    if (submissions === null) dispatch(getSubmissions(selectedDays));
    if (topViewedForms === null) dispatch(getTopViewedForms());
    if (topUsedThemes === null) dispatch(getTopUsedThemes());
  }, []);

  useEffect(() => {
    if (users !== null) {
      usersChartRef.current.innerHTML = '';

      const linePlot = new Line(usersChartRef.current, {
        title: {
          visible: true,
          text: 'Users'
        },
        padding: 'auto',
        forceFit: true,
        height: 300,
        data: users,
        xField: 'date',
        yField: 'value',
        seriesField: 'type',
        xAxis: {
          visible: false
        },
        responsive: true
      });

      linePlot.render();
    }

    if (forms !== null) {
      formsChartRef.current.innerHTML = '';

      const linePlot = new Line(formsChartRef.current, {
        title: {
          visible: true,
          text: 'Forms'
        },
        padding: 'auto',
        forceFit: true,
        height: 300,
        data: forms,
        xField: 'date',
        yField: 'value',
        seriesField: 'type',
        xAxis: {
          visible: false
        },
        responsive: true
      });

      linePlot.render();
    }

    if (submissions !== null) {
      submissionsChartRef.current.innerHTML = '';

      const linePlot = new Line(submissionsChartRef.current, {
        title: {
          visible: true,
          text: 'Submissions'
        },
        padding: 'auto',
        forceFit: true,
        height: 300,
        data: submissions,
        xField: 'date',
        yField: 'value',
        xAxis: {
          visible: false
        },
        responsive: true
      });

      linePlot.render();
    }
  }, [users, forms, submissions]);

  const menuOptions = [
    {
      id: 7,
      label: 'Last 7 days'
    },
    {
      id: 30,
      label: 'Last 30 days'
    },
    {
      id: 907,
      label: 'Last 90 days'
    },
    {
      id: 360,
      label: 'Last 360 days'
    }
  ];

  const menu = (
    <Menu>
      {menuOptions.map((option) => (
        <Menu.Item
          key={option.label}
          onClick={() => {
            setSelectedDays(option.id);
            reload(option.id);
          }}>
          {option.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      <PageHeader
        title="Statistics"
        extra={[
          <Dropdown overlay={menu} placement="bottomLeft">
            <Button>
              {menuOptions.find((option) => option.id === selectedDays).label} <DownOutlined />
            </Button>
          </Dropdown>,
          <Button onClick={() => reload()}>
            <ReloadOutlined />
          </Button>
        ]}
        style={{ border: '1px solid #f0f0f0', background: '#fff', margin: '0 0 15px 0' }}
      />

      <Card style={{ width: '100%' }}>
        <Spin spinning={getUsersLoading} size="large">
          <div style={{ minHeight: 306 }} ref={usersChartRef} />
        </Spin>

        <Spin spinning={getFormsLoading} size="large">
          <div style={{ minHeight: 306 }} ref={formsChartRef} />
        </Spin>

        <Spin spinning={getSubmissionsLoading} size="large">
          <div style={{ minHeight: 306 }} ref={submissionsChartRef} />
        </Spin>

        <Row gutter={16} style={{ margin: '40px 0 0 0' }}>
          <Col span={8}>
            <Spin spinning={getTopViewedFormsLoading}>
              {topViewedForms !== null && (
                <List
                  size="small"
                  header={<div>Top Viewed Forms</div>}
                  bordered
                  dataSource={topViewedForms}
                  renderItem={(item, index) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          item.form ? (
                            <Link to={`/forms/${item.form._id}`}>
                              {index + 1}. {item.form.name}
                            </Link>
                          ) : (
                            '[deleted]'
                          )
                        }
                        description={
                          item.form.owner ? <Link to={`/users/${item.form.owner._id}`}>{item.form.owner.email}</Link> : '[deleted]'
                        }
                      />
                      <div>{item.totalViews}</div>
                    </List.Item>
                  )}
                />
              )}
            </Spin>
          </Col>

          <Col span={8}>
            <Spin spinning={getTopUsedThemesLoading}>
              {topUsedThemes !== null && (
                <List
                  size="small"
                  header={<div>Top Used Themes</div>}
                  bordered
                  dataSource={topUsedThemes.sort((a, b) => b.count - a.count)}
                  renderItem={(item, index) => (
                    <List.Item>
                      <List.Item.Meta title={item._id} />
                      <div>{item.count}</div>
                    </List.Item>
                  )}
                />
              )}
            </Spin>
          </Col>

          <Col span={8}></Col>
        </Row>
      </Card>
    </>
  );
};

export default Statistics;
